/**
 * P-6 Operation-Queue (IndexedDB).
 *
 * Hält pending RPC-Calls während Offline. Separat von der Foto-Queue
 * (die große Blobs hält). Format pro Entry:
 *
 * {
 *   id,                // lokale UUID (PK)
 *   clientOperationId, // fachliche UUID = movements.client_operation_id etc.
 *   method,            // 'post' | 'patch' | 'delete'
 *   path,              // '/movements', '/receipts/:id/post', ...
 *   payload,           // Request-Body
 *   rpcName,           // lesbarer Name für UI (z.B. 'Entnahme')
 *   summary,           // kurze Zusammenfassung ('5 Stk · Regal A')
 *   status,            // 'pending' | 'syncing' | 'synced' | 'failed' | 'conflict'
 *   attempts, lastAttemptAt, lastError,
 *   createdAt,
 * }
 */

import { api, formatApiErrorDetail } from "./api";
import { getQueueUser, ownsEntry } from "./queueUser";

const DB_NAME = "platzwart_ops";
const DB_VERSION = 1;
const STORE = "operation_queue";

let _dbPromise = null;

function openDb() {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const s = db.createObjectStore(STORE, { keyPath: "id" });
                s.createIndex("status", "status", { unique: false });
                s.createIndex("createdAt", "createdAt", { unique: false });
                s.createIndex("clientOperationId", "clientOperationId", { unique: false });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return _dbPromise;
}

function tx(mode, fn) {
    return openDb().then(
        (db) =>
            new Promise((resolve, reject) => {
                const t = db.transaction(STORE, mode);
                const s = t.objectStore(STORE);
                const out = fn(s);
                t.oncomplete = () => resolve(out);
                t.onerror = () => reject(t.error);
                t.onabort = () => reject(t.error);
            })
    );
}

function uuid() {
    return crypto.randomUUID
        ? crypto.randomUUID()
        : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });
}

export async function listAll() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const t = db.transaction(STORE, "readonly");
        const req = t.objectStore(STORE).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });
}

/** #53: nur Einträge des angemeldeten Nutzers (UI-Liste). */
export async function listMine() {
    return (await listAll()).filter(ownsEntry);
}

export async function counts() {
    const all = await listMine();
    const c = { pending: 0, syncing: 0, synced: 0, failed: 0, conflict: 0 };
    for (const j of all) c[j.status] = (c[j.status] || 0) + 1;
    c.active = c.pending + c.syncing;
    c.unresolved = c.pending + c.syncing + c.failed + c.conflict;
    return c;
}

export async function update(id, patch) {
    return tx("readwrite", (s) => {
        const get = s.get(id);
        get.onsuccess = () => {
            const rec = get.result;
            if (!rec) return;
            Object.assign(rec, patch);
            s.put(rec);
        };
    });
}

export async function remove(id) {
    return tx("readwrite", (s) => s.delete(id));
}

/** Garbage-Collect synced entries older than 24 h. */
export async function gc() {
    const all = await listAll();
    const cutoff = Date.now() - 24 * 3600 * 1000;
    for (const rec of all) {
        if (rec.status === "synced" && new Date(rec.createdAt).getTime() < cutoff) {
            await remove(rec.id);
        }
    }
}

/**
 * enqueue({ method, path, payload, clientOperationId, rpcName, summary })
 * → writes entry with status 'pending', returns {id, clientOperationId}.
 * Caller liefert sofort weiter; sync läuft im Hintergrund.
 */
export async function enqueue(entry) {
    const id = uuid();
    const rec = {
        id,
        status: "pending",
        attempts: 0,
        lastAttemptAt: null,
        lastError: null,
        createdAt: new Date().toISOString(),
        // #53: Eintrag gehört dem angemeldeten Nutzer; nur dessen Sitzung
        // zeigt und versendet ihn (geteilte Lagergeräte).
        userId: getQueueUser(),
        ...entry,
    };
    await tx("readwrite", (s) => s.put(rec));
    return rec;
}

/**
 * Sync a single entry. Returns server response or throws.
 * Retries with backoff are handled by the worker (runSyncPass).
 */
export async function syncOne(rec) {
    await update(rec.id, {
        status: "syncing",
        lastAttemptAt: new Date().toISOString(),
    });
    try {
        const resp = await api.request({
            method: rec.method || "post",
            url: rec.path,
            data: rec.payload,
            // hängender Request darf den sequentiellen Sync nicht blockieren;
            // Timeout < Stale-Fenster (2 min), Backend-Replay ist idempotent
            timeout: 45_000,
        });
        // R12: Foto-Zuordnung gehört zum Sync-Erfolg -- auch wenn die
        // Operation erst später vom Worker nachgesendet wird.
        // #55: Fehler dabei nicht mehr still schlucken -- die Buchung ist
        // durch, aber der Eintrag zeigt den Verknüpfungsfehler an.
        let linkFailures = 0;
        if (rec.photos && rec.photos.length > 0 && resp.data?.id) {
            const results = await Promise.all(
                rec.photos
                    .filter((p) => p && p.photoId)
                    .map((p) =>
                        api
                            .patch(`/photos/${p.photoId}`, {
                                entity_type: p.entityType || "movement",
                                entity_id: resp.data.id,
                            })
                            .then(() => true)
                            .catch(() => false)
                    )
            );
            linkFailures = results.filter((ok) => !ok).length;
        }
        await update(rec.id, {
            status: "synced",
            lastError: linkFailures
                ? `${linkFailures} Foto(s) nicht verknüpft -- im Verlauf prüfen und melden.`
                : null,
        });
        return { ok: true, data: resp.data };
    } catch (e) {
        const status = e.response?.status;
        const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
        // R14: Session abgelaufen -> retryable lassen; nach Re-Login
        // sendet der Worker erneut (Backend-Replay ist idempotent).
        if (status === 401 || status === 403) {
            await update(rec.id, {
                status: "pending",
                attempts: (rec.attempts || 0) + 1,
                lastError: "Sitzung abgelaufen — bitte neu anmelden.",
            });
            return { ok: false, status, error: detail, retryable: true };
        }
        // Idempotenz-Konflikt: 409 vom Backend signalisiert abweichende Daten.
        if (status === 409) {
            await update(rec.id, {
                status: "conflict",
                attempts: (rec.attempts || 0) + 1,
                lastError: detail,
            });
            return { ok: false, status, error: detail };
        }
        // 4xx ausser 409/401/403 = Fachfehler -> failed, kein Retry.
        if (status && status >= 400 && status < 500) {
            await update(rec.id, {
                status: "failed",
                attempts: (rec.attempts || 0) + 1,
                lastError: detail,
            });
            return { ok: false, status, error: detail };
        }
        // 5xx oder Netzwerk -> pending, Backoff.
        await update(rec.id, {
            status: "pending",
            attempts: (rec.attempts || 0) + 1,
            lastError: detail,
        });
        return { ok: false, status, error: detail, retryable: true };
    }
}

/** Backoff: 1s, 2s, 4s, ... max 60s */
function backoffMs(attempts) {
    return Math.min(60_000, Math.pow(2, attempts) * 1000);
}

/**
 * runSyncPass: nimmt alle pending-Jobs (FIFO nach createdAt),
 * respektiert Backoff, synced sequentiell um RPC-Reihenfolge
 * (Receipt-draft vor add-item vor post) beizubehalten.
 */
/** R11: nach App-Kill/Reload hängengebliebene 'syncing'-Einträge
 *  zurücksetzen. Backend-Replay ist idempotent, erneutes Senden gefahrlos. */
const STALE_SYNCING_MS = 2 * 60 * 1000;

async function recoverStale(all) {
    const cutoff = Date.now() - STALE_SYNCING_MS;
    for (const rec of all) {
        if (rec.status !== "syncing") continue;
        const last = rec.lastAttemptAt ? new Date(rec.lastAttemptAt).getTime() : 0;
        if (!last || last < cutoff) {
            await update(rec.id, { status: "pending" });
            rec.status = "pending";
        }
    }
}

/** Reentrancy-Guard: Worker-Intervall und manuelles "Alle senden" liefen
 *  bisher parallel über dieselben pending-Records -- der Zweitversand
 *  endet dank Claim-Idempotenz im Backend als 409 und färbt den Eintrag
 *  fälschlich auf "Konflikt". */
let _passInFlight = null;

export function runSyncPass() {
    if (_passInFlight) return _passInFlight;
    _passInFlight = (async () => {
        // #53: abgemeldet wird nichts versendet; fremde Einträge bleiben
        // liegen, bis ihr Besitzer sich wieder anmeldet.
        if (!getQueueUser()) return;
        await gc();
        const all = await listAll();
        await recoverStale(all);
        for (const rec of all) {
            // Alt-Einträge (vor #53) beim ersten Kontakt stempeln.
            if (!rec.userId) {
                await update(rec.id, { userId: getQueueUser() });
                rec.userId = getQueueUser();
            }
        }
        const pending = all
            .filter((r) => r.status === "pending" && ownsEntry(r))
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        for (const rec of pending) {
            const last = rec.lastAttemptAt ? new Date(rec.lastAttemptAt).getTime() : 0;
            if (last && Date.now() - last < backoffMs(rec.attempts || 0)) continue;
            await syncOne(rec);
        }
    })().finally(() => {
        _passInFlight = null;
    });
    return _passInFlight;
}

/** Entry manually retry (from UI). */
export async function retry(id) {
    const db = await openDb();
    const rec = await new Promise((resolve, reject) => {
        const t = db.transaction(STORE, "readonly");
        const req = t.objectStore(STORE).get(id);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    if (!rec) return { ok: false, error: "Eintrag nicht gefunden." };
    await update(id, { status: "pending", attempts: 0, lastError: null });
    return syncOne({ ...rec, status: "pending", attempts: 0 });
}

export async function discard(id) {
    await remove(id);
}
